// @flow

import type {
  PropsSchema,
  EventSourceMap,
  EasyAction,
  BasicProps
} from '../../type'

import * as React from 'react'
import { loading, alertError } from 'frame/ui/popup'
import { mClient, abroadClient, operatorClient } from 'frame/net/clientFactory'
import computeData from '../hoc/computeData'

type Props = BasicProps & {
  url: string,
  method: 'get' | 'postJson' | 'postForm' | 'encryptPost',
  showLoading: boolean,
  events: EventSourceMap,
  reqParam?: string,
  client?: 'mclient' | 'backend' | 'backendRequireLogin' | 'abroadClient',
  formValues: { [string]: any },
  didUpdateCallApi?: boolean,
  didMountCallApi?: boolean,
  initialPropsCallApi?: boolean,
  customProps?: any
};

type ApiCallOpts = {
  path: string,
  method: 'get' | 'postJson' | 'postForm' | 'encryptPost',
  params: ?Object
};

@computeData({ includeData: true, includeVisible: false })
export default class CallApi extends React.PureComponent<Props> {
  static defaultProps = {
    isShow: true,
    client: 'mclient',
    method: 'get'
  };

  static schema: PropsSchema = [
    {
      type: 'select',
      key: 'client',
      title: '类型',
      options: [
        ['mclient', 'M 站 API'],
        ['backend', '后端 API'],
        ['backendRequireLogin', '后端 (必须登录)'],
        ['abroadClient', '海外 API'],
        ['operatorClient', '运营商 API']
      ],
      defaultValue: 'mclient'
    },
    {
      key: 'url',
      type: 'textarea',
      title: '路径'
    },
    {
      type: 'select',
      key: 'method',
      title: '方法',
      options: ['get', 'postJson', 'postForm', 'postFormData', 'encryptPost'],
      defaultValue: 'get'
    },
    {
      title: '请求参数',
      key: 'reqParam',
      type: 'code'
    },
    {
      type: 'boolean',
      key: 'showLoading',
      title: '显示加载效果'
    },
    {
      type: 'boolean',
      key: 'didMountCallApi',
      title: '就绪自动调用'
    },
    {
      type: 'boolean',
      key: 'initialPropsCallApi',
      title: '服务端渲染调用'
    },
    'separator',
    {
      type: 'event',
      title: '成功',
      event: 'resolve'
    },
    {
      type: 'event',
      title: '失败',
      event: 'reject'
    }
  ];

  static actions: EasyAction[] = [
    {
      title: '调用 API',
      name: 'callApi'
    }
  ];

  componentDidMount () {
    const { didMountCallApi, initialPropsCallApi } = this.props
    if (didMountCallApi || initialPropsCallApi) {
      this.actionCallApi()
    }
  }

  componentDidUpdate (preProps: Props) {
    if (this.props.didMountCallApi) {
      // 就绪自动执行的api 在复杂组件配置的内容发生变化时自动调用
      const { $comp } = this.props.customProps || {}
      const { $comp: preComp } = preProps.customProps || {}
      if ($comp && preComp && preComp !== $comp) {
        for (let key in $comp) {
          if ($comp[key] !== preComp[key]) {
            this.actionCallApi()
            return
          }
        }
      }
    }
  }

  loading = <T>(promise: Promise<T>, showLoading: boolean): Promise<T> => {
    return showLoading ? loading(promise) : promise
  };

  getApiCallOpts = (): ApiCallOpts => {
    const { method, reqParam: reqParamCode, jsEnv, client } = this.props
    const oriPath = jsEnv.getString(this.props.url)
    const reqParam = reqParamCode ? jsEnv.runCode(reqParamCode) : undefined
    const opts: ApiCallOpts = {
      path: oriPath,
      method,
      params: reqParam
    }

    if (client === 'backend' || client === 'backendRequireLogin') {
      // set path
      opts.path = `/v2/common/backend${this.getUrlPrefix(method)}${
        client === 'backendRequireLogin' ? 'RequireLogin' : ''
      }`
      opts.params = ({ path: oriPath }: Object)

      // set params, method (if need)
      if (method === 'postJson') {
        opts.params.json = reqParam
      } else if (method === 'postForm') {
        opts.method = 'postJson'
        opts.params.form = reqParam
      } else if (method === 'get') {
        Object.assign(opts.params, reqParam)
      } else if (method === 'encryptPost') {
        opts.params.form = reqParam
      } else if (method === 'postFormData') {
        Object.assign(opts.params, reqParam)
      }
    }
    if (reqParam && reqParam._intraType) {
      opts.path = `/v2/intra/backend${this.getUrlPrefix(method)}`
      opts.params = Object.assign(opts.params || {}, {
        type: reqParam._intraType
      })
    }
    return opts
  };

  getUrlPrefix = (method: string) => {
    let urlPrefix = 'Get'
    if (
      method === 'postForm' ||
      method === 'postJson' ||
      method === 'encryptPost'
    ) {
      urlPrefix = 'Post'
    } else if (method === 'postFormData') {
      urlPrefix = 'PostFormData'
    }
    return urlPrefix
  };

  actionCallApi = (data: void, dynamicData: any) => {
    const { showLoading, callActions, setCompInputData } = this.props
    setCompInputData(dynamicData, () => {
      const { method, params, path } = this.getApiCallOpts()

      let apiClient = mClient

      if (this.props.client === 'abroadClient') {
        apiClient = abroadClient
      } else if (this.props.client === 'operatorClient') {
        apiClient = operatorClient
      }

      // $FlowFixMe
      const reqPromise: Promise<*> = apiClient[method](path, params)

      this.loading(reqPromise, showLoading)
        .then(data => {
          callActions('resolve', data)
        })
        .catch(e => {
          callActions('reject', e.opts)
          if (e.code === 0) {
            alertError('网络异常')
          }
        })
    })
  };

  render () {
    return null
  }
}



// WEBPACK FOOTER //
// ./src/projects/easy/public/Comps/program/CallApi.js